import { useState } from 'react'

import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert, SeverityPill, AssetDetailModal } from '@/components/ui'
import { useAlarmCodes, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import alarmLocationDrilldown from '@/insights/common/alarmLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import LiveDuration from '@/insights/components/LiveDuration'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import SingleValueDisplay from '@/insights/components/SingleValueDisplay'
import { ApiService } from '@/providers/ApiProvider'
import {
  InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { Alarm } from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

export interface ReeferAlarmsActiveParams extends InsightParametersType {
  companyId?: number
  severity?: string[]
  geofenceIds?: number[]
  geogenceCategoryIds?: number[]
  codeId?: number[]
  date?: string
  from?: string
  to?: string
  displayFormat: string
  activeSince?: string // deprecated but keeping for previous configs
}
const ReeferAlarmActiveBySeverity: InsightDefinition<ReeferAlarmsActiveParams, Alarm[] | number> = {
  active: true,
  slug: 'alarm/alarms-active',
  category: 'reeferAlarm',
  name: 'Active Reefer Alarms',
  description: 'List active alarms, filtered by severity or alarm type',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,
  refreshInterval: 60000,

  configuration: {
    // called after each parameter set; for this one we want to allow
    // either severity OR alarm code so clear one when the other changes.
    transform: ({
      parameters,
      field,
    }: {
      parameters: ReeferAlarmsActiveParams
      field: keyof ReeferAlarmsActiveParams
    }) => {
      if (field === 'codeId') {
        return { ...parameters, severity: undefined }
      }
      if (field === 'severity') {
        return { ...parameters, codeId: undefined }
      }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }

      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alarm Code' },
      { id: 'codeId', control: 'AlarmCodeDropdown', required: false, label: 'Alarm', showCode: true },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        label: 'Active Since',
        options: DateOptions,
      },
      {
        id: 'from',
        label: 'Active Since Date',
        control: 'DateSelect',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
      {
        id: 'displayFormat',
        control: 'CustomSingleSelect',
        options: [
          { label: 'Table', value: 'table', isDefault: true },
          { label: 'Single Value Chart', value: 'single' },
        ],
      },
    ],

    isValid: (configuration: ReeferAlarmsActiveParams) => {
      if (configuration.activeSince && !configuration.from && configuration.date === 'custom') {
        configuration.from = configuration.activeSince
      }
      if (configuration.date === 'custom' && !configuration.from) {
        return 'Enter the custom date for Active Since'
      }
      if (configuration.date === 'custom' && configuration.from) {
        const activeSinceDate = new Date(configuration.from)
        const thirteenMonthsAgo = new Date()
        thirteenMonthsAgo.setMonth(thirteenMonthsAgo.getMonth() - 13)
        if (activeSinceDate < thirteenMonthsAgo) {
          return 'Active Since date must be within the last 13 Months'
        }
      }

      const hasCodeId = configuration.codeId && configuration.codeId.length > 0
      const hasSeverity = configuration.severity && configuration.severity.length > 0

      if (configuration?.companyId && (hasCodeId || hasSeverity)) {
        return true
      }
      if (configuration?.companyId) {
        return 'Alarm Code or Severity is required'
      }
      return 'Company and either Alarm Code or Severity are required.'
    },
  },

  load: async (api: ApiService, parameters: ReeferAlarmsActiveParams) => {
    let skipTake = '&skip=0&take=1000'
    if (parameters.displayFormat === 'single') {
      skipTake = '&skip=0&take=1'
    }
    const params = { ...parameters }
    if (params.activeSince && !params.from && params.date === 'custom') {
      params.from = params.activeSince
    }
    /*
    // Map activeSince to from parameter for backend compatibility
    if (params.activeSince) {
      params.from = params.activeSince
      delete params.activeSince
    }
    */
    const path = `/insights/alarms/alarms-active?${buildQuery(params)}${skipTake}`
    const response = await api.get<Alarm[]>({
      path,
      origin: 'AlarmActiveBySeverity#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    if (parameters.displayFormat === 'single') {
      return response.count || 0
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferAlarmsActiveParams, title: string) => {
    const path = '/insights/alarms/alarms-active'
    const params = { ...parameters }
    // Map activeSince to from parameter for backend compatibility
    if (params.activeSince && !params.from && params.date === 'custom') {
      params.from = params.activeSince
      delete params.activeSince
    }
    await downloadCsv(api, path, params, title)
  },

  Display: ({ status, data, configuration }) => {
    const { companies } = useCompany()
    const parameters = configuration.parameters as ReeferAlarmsActiveParams
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      return <TableSkeleton columns={6} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }

    if (parameters.displayFormat === 'single') {
      const company = companies.find((c) => c.id === parameters.companyId)
      const companyName = company ? company.name : ''
      const ddConfig: InsightConfigurationType = {
        ...configuration,
        slug: 'alarm/alarms-active',
        title: `${companyName} ${configuration.title}: Details`,
        parameters: {
          ...parameters,
          displayFormat: 'table',
          status: 'active',
        },
      }

      return <SingleValueDisplay value={data as number} width={configuration.width} drilldownConfiguration={ddConfig} />
    }

    const alarms = data as Alarm[]
    if (!alarms?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const uniqueCompanies = new Set(alarms.map((alarm) => alarm.company.id))
    const shouldShowCompany = (parameters.isDrilldown && uniqueCompanies.size > 1) || uniqueCompanies.size > 1

    const body = alarms
      ?.sort((a, b) => {
        const t = b.raisedTime.getTime() - a.raisedTime.getTime()
        if (t === 0) {
          return a.asset.name.localeCompare(b.asset.name)
        }
        return t
      })
      .map((alarm) => ({
        ...(shouldShowCompany && {
          Company: <span>{alarm.company.name}</span>,
        }),
        Asset: (
          <button
            onClick={() => setSelectedAsset(alarm.asset.id)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {alarm.asset.name}
          </button>
        ),
        Alarm: (
          <span className="block text-left">
            {alarm.alarmCode.description} ({alarm.alarmCode.code})
          </span>
        ),
        Raised: {
          id: `raised-${alarm.id}`,
          date: alarm.raisedTime,
          display: DateTime.fromJSDate(alarm.raisedTime).toFormat('yyyy-MM-dd HH:mm'),
        },
        Duration: {
          value: (alarm.clearedTime || new Date()).getTime() - alarm.raisedTime.getTime(),
          display: (
            <LiveDuration
              raisedTime={alarm.raisedTime}
              clearedTime={alarm.clearedTime}
              className="block text-left min-w-fit"
            />
          ),
        },
        Severity: {
          value: alarm.alarmCode.severityCode,
          display: (
            <SeverityPill
              label={getSeverityLabel(String(alarm.alarmCode.severityCode))}
              severityCode={alarm.alarmCode.severityCode}
            />
          ),
        },
        Geofence: alarmLocationDrilldown(alarm, configuration, 'raisedGeofence', parameters.companyId, ''),
        Address: alarmLocationDrilldown(alarm, configuration, 'raised', parameters.companyId),
      }))

    const dataItem = body?.[0]

    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(3)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    const content = (
      <section className="text-base-content text-base h-full flex flex-col">
        {/* {showDescription && <ContentDescription companies={companies} params={params} message={message} />} */}
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
    return content
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const codes = useAlarmCodes()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return 'Error'
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as ReeferAlarmsActiveParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.code) {
      fields['Alarm Code'] = codes.find((c) => c.id === Number(params.code))?.description || ''
    }
    if (params.severity?.length) {
      const p = typeof params.severity === 'string' ? [params.severity] : params.severity
      const labels = p
        .map((s) => getSeverityLabel(s))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (fields['Date']) {
      fields['Date'] = `Active Since: ${fields['Date']}`
    }
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status, configuration }) => {
    if (configuration.parameters.displayFormat === 'single') {
      return ''
    }
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = (data as Alarm[])?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferAlarmActiveBySeverity
